import React, { useMemo, useState } from "react"
import { Alert } from "antd"
import { Trans, useTranslation } from "react-i18next"
import { useEmployees } from "Shared/queries/employee.queries"
import { useAuth } from "Shared/hooks/auth.hooks"
import { Employee } from "@finway-group/shared/lib/models/user/employee.model"
import { TFunction } from "i18next"
import { formatDate } from "Shared/utils/date.utils"
import { CaretRightOutlined, CaretUpOutlined } from "@ant-design/icons"

const getSubstituting = (employee: Employee, t: TFunction) => {
    const name = employee.getFullName()
    const { startDate, endDate } = employee?.absence || {}
    if (startDate && endDate) {
        return (
            <Trans
                t={t}
                i18nKey="info:active_substitution_banner.substituting_name_period"
                values={{
                    name,
                    start_date: formatDate(startDate),
                    end_date: formatDate(endDate),
                }}
            />
        )
    }
    return <strong>{name}</strong>
}

export const ActiveSubstitutionBanner = () => {
    const { t } = useTranslation()
    const { userClass } = useAuth()
    const { data: employees = [] } = useEmployees({ excludeDeleted: true })
    const substitutingFor = userClass?.substitutingFor || []
    const [isExpanded, setIsExpanded] = useState(false)
    const substitutedEmployees = useMemo<Array<Employee>>(() => employees.filter((e) => substitutingFor.includes(e.id)), [employees, substitutingFor])
    if (!substitutedEmployees.length) {
        return <></>
    }

    const displayedEmployees = isExpanded ? substitutedEmployees : substitutedEmployees.slice(0, 3)

    return (
        <div className="active-substitution-banner">
            <Alert
                className="mb-2"
                type="info"
                showIcon
                message={<span className="font-bold">{t("info:active_substitution_banner:title")}</span>}
                description={
                    <>
                        {displayedEmployees.length === 1 && (
                            <>
                                <Trans
                                    t={t}
                                    i18nKey="info:active_substitution_banner.substituting_1"
                                    values={{
                                        name: displayedEmployees[0].getFullName(),
                                        start_date: displayedEmployees[0].absence?.startDate ? formatDate(displayedEmployees[0].absence?.startDate) : "",
                                        end_date: displayedEmployees[0].absence?.endDate ? formatDate(displayedEmployees[0].absence?.endDate) : "",
                                    }}
                                />
                                <br />
                            </>
                        )}
                        {displayedEmployees.length > 1 && (
                            <>
                                <Trans t={t} i18nKey="info:active_substitution_banner.you_substituting" values={{ count: substitutedEmployees.length }} />
                                {displayedEmployees.map((employee) => (
                                    <div key={employee.id}>{getSubstituting(employee, t)}</div>
                                ))}
                            </>
                        )}
                        {substitutedEmployees.length > 3 && (
                            <div className="cursor-pointer mt-2 flex items-center" onClick={() => setIsExpanded(!isExpanded)}>
                                {isExpanded ? (
                                    <>
                                        <CaretUpOutlined className="mr-1 text-gray-900" />
                                        <span className="text-gray-900">{t("label:show_less")}</span>
                                    </>
                                ) : (
                                    <>
                                        <CaretRightOutlined className="mr-1 text-gray-900" />
                                        <span className="text-gray-900">
                                            {t("label:show_more")} ({substitutedEmployees.length - 3})
                                        </span>
                                    </>
                                )}
                            </div>
                        )}
                        {t("info:active_substitution_banner.request_in_todo")}
                    </>
                }
            />
        </div>
    )
}
